// frontend-react/src/components/player/ProjectionEngineSwitcher.js
// Chunk 30 Model Switcher — 3-way segmented control on the player-card projections
// section. Selects which engine's projection the card shows (consensus default).
// Copy ratified by Tony (Chunk 30). Disagreement chip is a hook for Chunk 31's full
// disagreement surface.
import React, { useState } from 'react';
import { dynastyTheme } from '../../services/colorService';

const C = dynastyTheme.tokens.colors;

// Ratified plain-language explainer copy (Pattern Spotter / Cautious Forecaster / Consensus).
export const ENGINES = [
  {
    key: 'consensus',
    label: 'Consensus',
    blurb: 'The balanced read — and our default.',
    panel:
      'Consensus blends both engines: the responsiveness of Pattern Spotter and the discipline ' +
      'of Cautious Forecaster. When the two agree, you can lean on the number with confidence. ' +
      'When they disagree, that gap is the signal — it means the player is genuinely hard to call ' +
      '(a breakout that might be real, a veteran who might be declining), and it’s a flag to dig ' +
      'deeper before you commit a roster spot or a trade. Best for everyday decisions; switch to a ' +
      'single engine only when you have a specific reason.',
  },
  {
    key: 'pattern_spotter',
    label: 'Pattern Spotter',
    blurb: 'Reads the hot and cold streaks.',
    panel:
      'Pattern Spotter leans on what a player has actually been doing lately — exit velocity, ' +
      'contact quality, recent form — to project the rest of the season. It reacts fast, so it’s ' +
      'the one to check when a player has clearly changed (a swing fix, a velocity bump, a new role). ' +
      'The trade-off: it can over-trust a hot or cold stretch that won’t last. Best when you want ' +
      'the most responsive read on a player who’s trending.',
    // DD-RO-BUGS6-0725 B3: the default panel's physical-signal examples ("exit velocity, contact
    // quality... a swing fix") are hitter language; on a pitcher card that read as a copy leak. Pitcher
    // variant uses pitching signals. (Consensus / Cautious copy is type-neutral and needs no variant.)
    panelPitcher:
      'Pattern Spotter leans on what a pitcher has actually been doing lately — velocity, whiff and ' +
      'contact rates, recent form — to project the rest of the season. It reacts fast, so it’s the ' +
      'one to check when a pitcher has clearly changed (a mechanical fix, a velocity bump, a move ' +
      'between the rotation and the bullpen). The trade-off: it can over-trust a hot or cold stretch ' +
      'that won’t last. Best when you want the most responsive read on a pitcher who’s trending.',
  },
  {
    key: 'cautious_forecaster',
    label: 'Cautious Forecaster',
    blurb: 'Plays it honest about what we don’t know.',
    panel:
      'Cautious Forecaster pulls every player toward what similar players have done, and it’s ' +
      'upfront about uncertainty — its range is wide when a player has a short or noisy track record, ' +
      'tight when the history is deep. It won’t chase a two-week hot streak. The trade-off: it can ' +
      'be slow to credit a real breakout. Best when you want a steady, regression-aware number and a ' +
      'clear sense of the risk around it.',
  },
];

const ProjectionEngineSwitcher = ({ engine, onSelect, disagreement = false, allAgree = false, isPitcher = false }) => {
  const [openInfo, setOpenInfo] = useState(false);
  const active = ENGINES.find((e) => e.key === engine) || ENGINES[0];
  // DD-RO-BUGS6-0725 B3: prefer the pitcher-flavored panel on a pitcher card when one exists.
  const panelText = (isPitcher && active.panelPitcher) || active.panel;

  return (
    <div className="projection-engine-switcher" style={{ margin: '8px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div role="tablist" aria-label="Projection engine"
             style={{ display: 'inline-flex', border: '1px solid rgba(55,65,81,0.5)', borderRadius: 8, overflow: 'hidden' }}>
          {ENGINES.map((e) => {
            const selected = e.key === engine;
            return (
              <button key={e.key} role="tab" aria-selected={selected} onClick={() => onSelect(e.key)}
                title={e.blurb}
                style={{
                  padding: '6px 12px', border: 'none', cursor: 'pointer', fontSize: 13,
                  background: selected ? C.primary : 'transparent',
                  color: selected ? C.black : C.neutral[400], fontWeight: selected ? 600 : 400,
                }}>
                {e.label}
              </button>
            );
          })}
        </div>
        <button onClick={() => setOpenInfo((v) => !v)} aria-label="What do these mean?"
          style={{ border: '1px solid rgba(55,65,81,0.5)', background: 'transparent', color: C.neutral[400],
                   borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 12 }}>
          {'ⓘ'}
        </button>
        {disagreement && engine === 'consensus' && (
          <span title="Pattern Spotter and Cautious Forecaster disagree on this player"
            style={{ fontSize: 12, color: C.warning, border: `1px solid ${C.warning}`,
                     borderRadius: 6, padding: '2px 6px' }}>
            engines disagree
          </span>
        )}
        {allAgree && !disagreement && engine === 'consensus' && (
          <span title="Pattern Spotter and Cautious Forecaster land on the same projection here — high confidence, no disagreement to arbitrate"
            style={{ fontSize: 12, color: C.success, border: `1px solid ${C.success}`,
                     borderRadius: 6, padding: '2px 6px' }}>
            engines agree
          </span>
        )}
      </div>
      {openInfo && (
        <div style={{ marginTop: 8, padding: 10, background: 'rgba(23,23,23,0.5)', border: '1px solid rgba(55,65,81,0.5)',
                      borderRadius: 8, maxWidth: 560 }}>
          <div style={{ color: C.neutral[200], fontWeight: 600, marginBottom: 4 }}>
            {active.label} — <span style={{ fontWeight: 400, color: C.neutral[400] }}>{active.blurb}</span>
          </div>
          <div style={{ color: C.neutral[200], fontSize: 13, lineHeight: 1.5 }}>{panelText}</div>
        </div>
      )}
    </div>
  );
};

export default ProjectionEngineSwitcher;
